import { useEffect, useState } from "react";
import { BookOpen, HelpCircle, BookA, Mic, MessageCircle, Flame, Award, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/ProtectedRoute";
import Subscription from "@/components/Subscription";
import DataPrivacy from "@/components/DataPrivacy";

const STATS = [
  ["stories_read", "Histoires lues", BookOpen, "var(--terracotta)"],
  ["words_discovered", "Mots découverts", BookA, "var(--sage)"],
  ["correct_answers", "Bonnes réponses", HelpCircle, "var(--sky)"],
  ["repeats_done", "Phrases répétées", Mic, "var(--sun)"],
  ["expressions_done", "Fois où j'ai raconté", MessageCircle, "var(--blush)"],
  ["streak", "Jours d'affilée", Flame, "var(--terracotta)"],
];

const LABELS = { story_read: "a lu", quiz: "a fait le quiz de", vocab: "a appris un mot de", repeat: "a répété une phrase de", expression: "a raconté autour de" };

export default function Profile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/progress").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader />;
  const unlocked = data.badges.filter((b) => b.unlocked).length;

  return (
    <main data-testid="profile-page" className="mx-auto max-w-7xl px-4 pb-24 pt-10 sm:px-8">
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-bold text-[color:var(--sage)]">{user.child_name}{user.child_age ? `, ${user.child_age} ans` : ""}</p>
          <h1 className="font-display text-4xl font-bold sm:text-5xl">Mes progrès</h1>
        </div>
        <p data-testid="profile-badge-count" className="rounded-full bg-[color:var(--espresso)] px-5 py-2 font-display text-lg text-[color:var(--paper)]">{unlocked} / {data.badges.length} badges</p>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {STATS.map(([k, label, Icon, color], i) => (
          <div key={k} data-testid={`stat-${k}`} className="rise card-paper p-5" style={{ animationDelay: `${i * 60}ms` }}>
            <Icon size={22} style={{ color }} />
            <p className="mt-2 font-display text-4xl font-bold">{data.stats[k]}</p>
            <p className="text-sm font-bold text-[color:var(--muted-ink)]">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <section className="lg:col-span-8">
          <h2 className="font-display text-3xl font-bold">Mes badges</h2>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {data.badges.map((b, i) => (
              <div key={b.id} data-testid={`badge-${b.id}`} title={b.description} style={{ animationDelay: `${i * 50}ms` }}
                className={`rise card-paper flex flex-col items-center p-4 text-center ${b.unlocked ? "" : "opacity-50 grayscale"}`}>
                <span className="grid h-16 w-16 place-items-center rounded-full text-white" style={{ background: b.unlocked ? "linear-gradient(135deg,#F6B343,#E59819)" : "#CFC3B3" }}>
                  {b.unlocked ? <Award size={30} /> : <Lock size={24} />}
                </span>
                <p className="mt-3 font-display font-bold leading-tight">{b.name}</p>
                <p className="mt-1 text-xs text-[color:var(--muted-ink)]">{b.description}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="lg:col-span-4">
          <h2 className="font-display text-3xl font-bold">Espace Parents</h2>
          <Subscription />
          <DataPrivacy />
          <div data-testid="profile-recent" className="card-paper mt-5 divide-y-2 divide-[color:var(--paper-2)] p-2">
            {data.recent.length === 0 && <p className="p-4 text-[color:var(--muted-ink)]">Aucune activité pour l'instant. Lisez une première histoire !</p>}
            {data.recent.map((e) => (
              <div key={e.id} className="p-3">
                <p className="font-semibold">{user.child_name} {LABELS[e.type]} <em>{e.story_title}</em>{e.type === "quiz" ? ` (${e.score}/${e.total})` : ""}</p>
                <p className="text-xs text-[color:var(--muted-ink)]">{new Date(e.created_at).toLocaleString("fr-FR")}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
